import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getHandoffToken } from "@/lib/handoffToken";
import { useAuth } from "@/context/AuthContext";
import { InstallStep, browserName, isNonChromiumAndroid } from "@/components/InstallStep";
import { ContinueInChrome } from "@/components/ContinueInChrome";
import { ShieldCheck, CircleNotch, WarningCircle, LockKey } from "@phosphor-icons/react";

const inStandaloneApp = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

function PinField({ label, value, onChange, testid }) {
  return (
    <>
      <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground text-left">{label}</label>
      <input
        data-testid={testid} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
        autoComplete="one-time-code" required value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-center text-2xl tracking-[0.6em] rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </>
  );
}

export default function MobileSetup() {
  // Chrome-handoff token: captured (and stripped from the URL/history) by
  // lib/handoffToken.js before the router boots. Memory only.
  const [handoffToken] = useState(getHandoffToken);
  const [state, setState] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("ub")) return "handoff";
    return p.get("e") ? "error" : "ready";
  });
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (pin.length !== 6) { setMessage("The Rotary Access PIN must be exactly 6 digits."); return; }
    if (pin !== confirm) { setMessage("The PINs do not match."); return; }
    setBusy(true);
    try {
      const r = await api.post("/devices/pin/setup", { pin });
      await refresh();
      if (inStandaloneApp()) {
        // Setup completed inside the installed app — go straight in.
        navigate(r.data?.dest || "/", { replace: true });
        return;
      }
      // In the browser: never show the dashboard here. Move to the blocking
      // install step and invoke Chrome's native install dialog immediately.
      window.history.replaceState(null, "", "/mobile-setup");
      setState("install");
    } catch (err) {
      if (err?.response?.status === 401) { setState("error"); return; }
      const d = err?.response?.data?.detail;
      setMessage(typeof d === "string" ? d : "Setup was not completed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="mobile-setup-page">
      <div className="max-w-md w-full border border-border bg-surface rounded-md p-8 text-center">
        <div className="h-10 w-10 bg-primary rounded flex items-center justify-center mx-auto">
          <ShieldCheck size={22} weight="fill" className="text-white" />
        </div>

        {state === "handoff" ? (
          <ContinueInChrome token={handoffToken} />
        ) : state === "install" ? (
          <InstallStep />
        ) : state === "error" ? (
          <>
            <div className="mt-5 mx-auto h-10 w-10 flex items-center justify-center text-amber-600">
              <WarningCircle size={32} weight="duotone" />
            </div>
            <h1 className="mt-3 t-section-title" data-testid="pairing-error-title">Pairing unavailable</h1>
            <p className="mt-2 t-support text-muted-foreground">
              This pairing code is invalid, expired or already used. Generate a new QR code from
              Get Mobile Access on your signed-in device.
            </p>
          </>
        ) : (
          <form onSubmit={submit} data-testid="pin-setup-form">
            <LockKey size={40} weight="duotone" className="text-primary mx-auto mt-5" />
            <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="mobile-setup-title">
              Secure this device to continue
            </h1>
            <p className="mt-3 t-support text-muted-foreground text-left">
              Before entering the Rotary GOV Report Builder, create a six-digit <b>Rotary Access PIN</b> for
              this phone. You will use it to unlock the app each time it opens.
            </p>
            <p className="mt-2 t-support text-muted-foreground text-left">
              This protects Rotary and club information. The PIN belongs to this device only —
              it will not work anywhere else.
            </p>
            {isNonChromiumAndroid() && !inStandaloneApp() && (
              <p data-testid="browser-warning" className="mt-3 p-3 border border-amber-300 bg-amber-50 text-amber-900 text-sm text-left rounded-md">
                You are using <b>{browserName()}</b>. The final installation step requires{" "}
                <b>Google Chrome</b>. To avoid repeating setup, open Chrome now and scan a fresh
                QR code from Get Mobile Access.
              </p>
            )}
            <PinField label="Create Rotary Access PIN" value={pin} onChange={setPin} testid="pin-setup-input" />
            <PinField label="Confirm PIN" value={confirm} onChange={setConfirm} testid="pin-confirm-input" />
            {message && <p data-testid="pin-setup-error" className="mt-3 text-sm text-red-700">{message}</p>}
            <button
              data-testid="pin-setup-submit-button" type="submit" disabled={busy}
              className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {busy ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <LockKey size={18} weight="bold" />}
              Complete Setup
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
